<template>
  <div class="timer-code-page" :class="themeClass">
    <div class="timer-code-shell">
      <div v-if="loading" class="panel panel--neutral">{{ $t('common.loading') || 'Cargando...' }}</div>
      <div v-else-if="error" class="panel panel--error">{{ error }}</div>
      <div v-else-if="isWaiting" class="panel panel--neutral panel--waiting">
        <div class="waiting-message">
          <span class="waiting-text">{{ waitingDisplay }}</span>
          <span v-if="waitingCompetitionName" class="waiting-competition">{{ waitingCompetitionName }}</span>
        </div>
      </div>
      <template v-else-if="isVs">
        <TimerVsDisplay
          :left-athlete="leftAthlete"
          :right-athlete="rightAthlete"
          :active-side="activeVsSide"
          :left-display="laneRemainingDisplay('left')"
          :left-remaining-seconds="laneRemainingSeconds('left')"
          :left-state-value="laneStateValue('left')"
          :left-is-recording="laneIsRecording('left')"
          :right-display="laneRemainingDisplay('right')"
          :right-remaining-seconds="laneRemainingSeconds('right')"
          :right-state-value="laneStateValue('right')"
          :right-is-recording="laneIsRecording('right')"
          :left-overtime="isLaneOvertime('left')"
          :right-overtime="isLaneOvertime('right')"
          :left-state-label="laneStateLabel('left')"
          :right-state-label="laneStateLabel('right')"
          :left-state-class="laneStateClass('left')"
          :right-state-class="laneStateClass('right')"
          :theme="theme"
          :layout-debug="layoutDebugEnabled"
        />
      </template>
      <template v-else>
        <TimerSingleDisplay
          :athlete="leftAthlete"
          :phase-label="currentPhaseLabel"
          :remaining-display="remainingDisplay"
          :remaining-seconds="singleRemainingSeconds"
          :is-overtime="isOvertime"
          :state-value="singleStateValue"
          :is-recording="singleIsRecording"
          :state-label="stateLabel"
          :state-class="stateClass"
          :theme="theme"
          :layout-debug="layoutDebugEnabled"
        />
      </template>

      <button class="fullscreen-btn fullscreen-btn--corner" @click="toggleFullscreen" :title="fullscreenTitle">
        <i :class="isFullscreen ? 'fa-solid fa-down-left-and-up-right-to-center' : 'fa-solid fa-up-right-and-down-left-from-center'"></i>
      </button>
    </div>
  </div>
</template>

<script>
import echoService from '@/services/echoService';
import TimerSingleDisplay from '@/components/timer/TimerSingleDisplay.vue';
import TimerVsDisplay from '@/components/timer/TimerVsDisplay.vue';
import {
  ALLOWED_TIMER_THEMES,
  isCustomTimerTheme,
  normalizeTimerTheme,
} from '@/constants/timerThemes';

export default {
  name: 'TimerCodeView',
  components: {
    TimerSingleDisplay,
    TimerVsDisplay,
  },
  data() {
    return {
      accessCode: this.$route.params.accessCode || '',
      loading: true,
      error: '',
      waitingActive: false,
      timer: null,
      round: null,
      channel: null,
      tickHandle: null,
      isFullscreen: false,
      lastState: '',
      skipNextResync: false,
      lastVersion: 0,
      serverDriftMs: 0,
      competitionCode: '',
      competitionId: null,
      currentChannelName: '',
      waitingMessage: '',
      waitingCompetitionName: '',
      waitingActivateHandle: null,
      theme: 'dark',
      tickNowMs: Date.now(),
    };
  },
  computed: {
    channelFullName() {
      return this.channelName ? `timer-code.${this.channelName}` : '';
    },
    isWaiting() {
      return this.waitingActive || (this.timer?.state || '').toLowerCase() === 'waiting';
    },
    isOvertime() {
      const sourceTimer = this.isVs && this.activeVsTimer ? this.activeVsTimer : this.timer;
      return this.resolveTimerDisplaySeconds(sourceTimer) < 0;
    },
    panelClass() {
      return this.isOvertime ? 'panel--overtime' : 'panel--light';
    },
    isVs() {
      const roundNumberRaw = this.round?.round_number ?? this.timer?.round_number;
      const roundNumber = Number(roundNumberRaw);

      const competitionIsVsRaw = this.round?.competition?.is_vs
        ?? this.round?.competition?.isVs
        ?? this.round?.is_vs
        ?? this.round?.isVs
        ?? this.timer?.competition?.is_vs
        ?? this.timer?.competition?.isVs
        ?? this.timer?.is_vs
        ?? this.timer?.isVs
        ?? null;

      const competitionIsVs = competitionIsVsRaw === null || competitionIsVsRaw === undefined
        ? null
        : [true, 1, '1', 'true'].includes(competitionIsVsRaw);

      const dualQualificationRaw = this.round?.is_dual_qualification
        ?? this.round?.is_qualification_dual
        ?? this.round?.dual_qualification
        ?? this.round?.competition?.is_dual_qualification
        ?? this.round?.competition?.is_qualification_dual
        ?? this.round?.competition?.dual_qualification
        ?? this.timer?.is_dual_qualification
        ?? this.timer?.is_qualification_dual
        ?? this.timer?.dual_qualification;

      const dualQualification = [true, 1, '1', 'true'].includes(dualQualificationRaw);

      const explicitVs = Boolean(
        this.round?.is_vs
          ?? this.round?.isVs
          ?? this.timer?.is_vs
          ?? this.timer?.isVs
      );

      const hasTwoAthletes = Array.isArray(this.round?.athletes) && this.round.athletes.length >= 2;
      const hasTwoTimers = Array.isArray(this.round?.timers) && this.round.timers.length >= 2;

      const inferredVs = explicitVs || hasTwoAthletes || hasTwoTimers;

      if (roundNumber === 1) return false;
      if (roundNumber === 2 && dualQualification) return false;

      if (competitionIsVs === false) return false;
      if (inferredVs) return true;
      if (competitionIsVs === true) return true;

      return false;
    },
    isRunning() {
      return (this.timer?.state || '').toLowerCase() === 'running';
    },
    hasAnyRunningTimer() {
      if (!this.isVs) return this.isRunning;
      return Object.values(this.vsTimersByRoundId).some(
        (timer) => String(timer?.state || '').toLowerCase() === 'running'
      );
    },
    leftAthlete() {
      return this.extractAthlete('left');
    },
    rightAthlete() {
      return this.extractAthlete('right');
    },
    activeVsTimer() {
      if (!this.isVs) return null;
      const rid = this.timer?.round_id || this.timer?.roundId;
      const timers = Array.isArray(this.round?.timers) ? this.round.timers : [];
      if (rid && this.timer && String(this.timer.round_id || this.timer.roundId) === String(rid)) {
        return this.timer;
      }
      if (rid) {
        const match = timers.find((t) => String(t.round_id || t.roundId) === String(rid));
        if (match) return match;
      }
      return timers[0] || null;
    },
    activeVsAthlete() {
      if (!this.isVs) return null;
      const timers = Array.isArray(this.round?.timers) ? this.round.timers : [];
      const athletes = Array.isArray(this.round?.athletes) ? this.round.athletes : [];
      const activeTimer = this.activeVsTimer;
      if (!activeTimer) return athletes[0] ? this.normalizeAthlete(athletes[0]) : null;
      const idx = timers.findIndex((t) => String(t.round_id || t.roundId) === String(activeTimer.round_id || activeTimer.roundId));
      const rawAth = idx >= 0 ? athletes[idx] : athletes[0];
      return rawAth ? this.normalizeAthlete(rawAth) : null;
    },
    activeVsSide() {
      if (!this.isVs) return 'left';

      const activeRoundId = this.activeVsTimer?.round_id || this.activeVsTimer?.roundId;
      const athletes = Array.isArray(this.round?.athletes) ? this.round.athletes : [];
      const leftRoundId = athletes[0]?.round_id || athletes[0]?.roundId;
      const rightRoundId = athletes[1]?.round_id || athletes[1]?.roundId;

      if (activeRoundId && String(activeRoundId) === String(rightRoundId || '')) {
        return 'right';
      }

      if (activeRoundId && String(activeRoundId) === String(leftRoundId || '')) {
        return 'left';
      }

      return 'left';
    },
    vsTimersByRoundId() {
      const map = {};
      const timers = Array.isArray(this.round?.timers) ? this.round.timers : [];
      timers.forEach((item) => {
        const rid = item?.round_id || item?.roundId;
        if (!rid) return;
        map[String(rid)] = { ...item };
      });

      const activeRid = this.timer?.round_id || this.timer?.roundId;
      if (activeRid) {
        map[String(activeRid)] = { ...(map[String(activeRid)] || {}), ...(this.timer || {}) };
      }

      return map;
    },
    remainingDisplay() {
      if (this.isVs && this.activeVsTimer) {
        return this.formatSeconds(this.resolveTimerDisplaySeconds(this.activeVsTimer));
      }
      return this.formatSeconds(this.resolveTimerDisplaySeconds(this.timer));
    },
    singleRemainingSeconds() {
      if (this.isVs && this.activeVsTimer) {
        return this.resolveTimerDisplaySeconds(this.activeVsTimer);
      }
      return this.resolveTimerDisplaySeconds(this.timer);
    },
    durationDisplay() {
      const seconds = this.resolveTimerDurationSeconds(this.timer);
      return this.formatSeconds(seconds);
    },
    singleStateValue() {
      return (this.timer?.state || 'idle').toLowerCase();
    },
    singleIsRecording() {
      return [true, 1, '1', 'true'].includes(this.timer?.is_recording);
    },
    currentPhaseLabel() {
      const stage = this.resolveCurrentPhaseLabel();

      if (stage) return stage;

      return '';
    },
    stateLabel() {
      const state = (this.timer?.state || 'idle').toLowerCase();
      if (state === 'waiting') return this.$t?.('judge.timer.state_waiting')?.toUpperCase?.() || 'WAITING';
      return state.toUpperCase();
    },
    waitingDisplay() {
      if (this.waitingMessage) return this.waitingMessage;
      return 'Waiting for competitor selection';
    },
    stateClass() {
      const state = (this.timer?.state || '').toLowerCase();
      return {
        'status-pill--running': state === 'running',
        'status-pill--paused': state === 'paused',
        'status-pill--idle': !state || state === 'idle',
        'status-pill--finished': state === 'finished',
        'status-pill--waiting': state === 'waiting',
      };
    },
    fullscreenTitle() {
      return this.isFullscreen
        ? (this.$t('common.exit_fullscreen') || 'Salir de pantalla completa')
        : (this.$t('common.fullscreen') || 'Pantalla completa');
    },
    themeClass() {
      const normalizedTheme = this.normalizeTheme(this.theme);
      return [
        normalizedTheme === 'light' ? 'theme-light' : '',
        isCustomTimerTheme(normalizedTheme) ? 'theme-custom' : '',
        `theme-${normalizedTheme}`,
      ].filter(Boolean);
    },
    layoutDebugEnabled() {
      const rawValue = this.$route?.query?.layoutDebug;
      return ['1', 'true', 'yes', 'on'].includes(String(rawValue || '').toLowerCase());
    },
    channelName() {
      return this.competitionCode || this.accessCode;
    },
  },
  watch: {
    '$route.params.accessCode'(next) {
      this.accessCode = next || '';
      this.resetChannel();
      this.init();
    },
  },
  mounted() {
    this.clearLegacyTimerStorage();
    this.init();
    document.addEventListener('fullscreenchange', this.handleFullscreenChange);
    this.applyBodyThemeClass();
    document.body.classList.add('hide-app-nav');
  },
  beforeUnmount() {
    this.resetChannel();
    this.stopTick();
    this.clearPendingWaitingState();
    document.removeEventListener('fullscreenchange', this.handleFullscreenChange);
    this.clearBodyThemeClass();
    document.body.classList.remove('hide-app-nav');
  },
  methods: {
    clearPendingWaitingState() {
      if (!this.waitingActivateHandle) return;
      clearTimeout(this.waitingActivateHandle);
      this.waitingActivateHandle = null;
    },
    applyWaitingState({ resolvedMessage = '', compName = '' } = {}) {
      this.clearPendingWaitingState();
      this.waitingActive = true;
      this.round = null;
      this.timer = null;
      this.waitingMessage = resolvedMessage;
      this.waitingCompetitionName = compName;
      this.lastState = 'waiting';
      this.stopTick();
    },
    scheduleWaitingState(payload = {}) {
      this.clearPendingWaitingState();
      this.waitingActivateHandle = setTimeout(() => {
        this.applyWaitingState(payload);
      }, 160);
    },
    unwrapRealtimePayload(raw) {
      if (!raw) return raw;

      let next = raw;

      try {
        if (typeof next === 'string') {
          next = JSON.parse(next);
        }

        if (next?.data && typeof next.data === 'string') {
          next = { ...next, ...JSON.parse(next.data) };
        } else if (next?.data && typeof next.data === 'object') {
          next = { ...next, ...next.data };
        }

        if (next?.payload && typeof next.payload === 'object') {
          next = { ...next, ...next.payload };
        }
      } catch (e) {
        console.warn('unwrapRealtimePayload failed', e?.message || e);
      }

      return next;
    },
    normalizeTimestampMs(value) {
      if (value === null || value === undefined || value === '') return NaN;

      const numeric = Number(value);
      if (Number.isFinite(numeric) && numeric > 0) {
        return numeric < 1e11 ? numeric * 1000 : numeric;
      }

      const parsed = Date.parse(value);
      return Number.isFinite(parsed) ? parsed : NaN;
    },
    normalizeVersionMs(value, fallbackUpdatedAt = null) {
      const numeric = Number(value);
      if (Number.isFinite(numeric) && numeric > 0) {
        return numeric < 1e12 ? numeric * 1000 : numeric;
      }

      const parsed = this.normalizeTimestampMs(fallbackUpdatedAt);
      return Number.isFinite(parsed) ? parsed : 0;
    },
    normalizeIncomingTimer(rawTimer = {}, envelope = {}) {
      const timer = { ...rawTimer };
      const nowMs = Date.now();

      const roundId = Number(timer.round_id ?? timer.roundId ?? envelope.round_id ?? envelope.roundId);
      if (Number.isFinite(roundId) && roundId > 0) {
        timer.round_id = roundId;
        timer.roundId = roundId;
      }

      const durationMsRaw = Number(timer.duration_ms ?? envelope.duration_ms);
      const remainingMsRaw = Number(timer.remaining_ms_snapshot ?? envelope.remaining_ms_snapshot);
      const durationSeconds = Number(timer.duration_seconds ?? timer.duration);
      const remainingSeconds = Number(timer.remaining_seconds ?? timer.remaining);

      const durationMs = Number.isFinite(durationMsRaw)
        ? durationMsRaw
        : (Number.isFinite(durationSeconds) ? durationSeconds * 1000 : NaN);
      const remainingMsSnapshot = Number.isFinite(remainingMsRaw)
        ? remainingMsRaw
        : (Number.isFinite(remainingSeconds)
          ? remainingSeconds * 1000
          : (Number.isFinite(durationMs) ? durationMs : NaN));

      const serverNowMs = this.normalizeTimestampMs(timer.server_now_ms ?? envelope.server_now_ms ?? timer.server_now ?? envelope.server_now);
      const snapshotEmittedAtMs = this.normalizeTimestampMs(timer.snapshot_emitted_at_ms ?? envelope.snapshot_emitted_at_ms);
      const lastStartedAtMs = this.normalizeTimestampMs(timer.last_started_at_ms ?? timer.last_started_at ?? envelope.last_started_at_ms ?? envelope.last_started_at);
      const versionMs = this.normalizeVersionMs(timer.version_ms ?? envelope.version_ms ?? timer.version ?? envelope.version, timer.updated_at ?? envelope.updated_at);

      if (Number.isFinite(durationMs)) {
        timer.duration_ms = durationMs;
        timer.duration_seconds = durationMs / 1000;
        timer.duration = durationMs / 1000;
      }

      if (Number.isFinite(remainingMsSnapshot)) {
        timer.remaining_ms_snapshot = remainingMsSnapshot;
        if (!Number.isFinite(remainingSeconds)) {
          timer.remaining_seconds = remainingMsSnapshot / 1000;
        }
      }

      const normalizedState = String(timer.state || '').toLowerCase();
      if (normalizedState === 'paused' && Number.isFinite(timer.remaining_seconds)) {
        timer.remaining_ms_snapshot = timer.remaining_seconds * 1000;
      }

      if (Number.isFinite(serverNowMs)) {
        timer.server_now_ms = serverNowMs;
      }

      if (Number.isFinite(snapshotEmittedAtMs)) {
        timer.snapshot_emitted_at_ms = snapshotEmittedAtMs;
      } else if (Number.isFinite(serverNowMs)) {
        timer.snapshot_emitted_at_ms = serverNowMs;
      }

      if (Number.isFinite(lastStartedAtMs)) {
        timer.last_started_at_ms = lastStartedAtMs;
      }

      timer.is_recording = [true, 1, '1', 'true'].includes(
        timer.is_recording ?? envelope.is_recording
      );

      if (versionMs > 0) {
        timer.version_ms = versionMs;
        timer.version = versionMs;
      }

      timer.client_received_at_ms = Number(timer.client_received_at_ms) || nowMs;
      timer.schema_version = Number(timer.schema_version ?? envelope.schema_version) || timer.schema_version;

      if (!timer.state) timer.state = 'idle';
      return timer;
    },
    normalizeRoundPayload(round = {}, envelope = {}) {
      const timers = Array.isArray(round?.timers)
        ? round.timers.map((timer) => this.normalizeIncomingTimer(timer, envelope))
        : [];
      const directTimer = round?.timer && typeof round.timer === 'object'
        ? this.normalizeIncomingTimer(round.timer, envelope)
        : null;

      return {
        ...round,
        is_dual_qualification: round?.is_dual_qualification ?? envelope.is_dual_qualification,
        timer: directTimer,
        timers,
      };
    },
    buildWaitingSnapshotPayload(raw = {}, snapshotRound = null) {
      return {
        ...raw,
        status: 'waiting',
        timer: {
          state: 'waiting',
          remaining_seconds: 0,
          duration_seconds: 0,
          remaining_ms_snapshot: 0,
          duration_ms: 0,
          competition_code: raw?.access_code || raw?.accessCode || this.competitionCode || '',
          competition_name: snapshotRound?.competition?.name || raw?.competition_name || raw?.competitionName || '',
          is_dual_qualification: raw?.is_dual_qualification,
          version_ms: raw?.version_ms,
          version: raw?.version_ms ?? raw?.version,
          server_now_ms: raw?.server_now_ms,
          server_now: raw?.server_now,
          message: raw?.message,
        },
      };
    },
    hasRenderableSnapshotTimers(timers = []) {
      if (!Array.isArray(timers) || !timers.length) return false;

      return timers.some((timer) => {
        const state = String(timer?.state || '').toLowerCase();
        if (state === 'running' || state === 'paused' || state === 'finished') return true;

        return [
          timer?.remaining_ms_snapshot,
          timer?.remaining_seconds,
          timer?.duration_ms,
          timer?.duration_seconds,
          timer?.duration,
        ].some((value) => Number.isFinite(Number(value)));
      });
    },

    shouldPreserveVsSnapshotContext(snapshotRound = null, timers = []) {
      if (!snapshotRound || !this.hasRenderableSnapshotTimers(timers)) return false;

      const isVsRound = [true, 1, '1', 'true'].includes(
        snapshotRound?.is_vs
        ?? snapshotRound?.isVs
        ?? snapshotRound?.competition?.is_vs
        ?? snapshotRound?.competition?.isVs
      );
      const athleteCount = Array.isArray(snapshotRound?.athletes) ? snapshotRound.athletes.length : 0;

      return isVsRound || timers.length > 1 || athleteCount > 1 || this.isVs;
    },
    resolveSnapshotActiveTimer(timers = [], activeRoundId = null, hasActiveRoundKey = false) {
      if (!Array.isArray(timers) || !timers.length) return null;

      if (hasActiveRoundKey && activeRoundId !== null && activeRoundId !== undefined) {
        return timers.find((item) => String(item?.round_id || item?.roundId) === String(activeRoundId)) || null;
      }

      const currentRoundId = this.timer?.round_id || this.timer?.roundId;
      if (currentRoundId) {
        const currentTimer = timers.find((item) => String(item?.round_id || item?.roundId) === String(currentRoundId));
        if (currentTimer) return currentTimer;
      }

      const recordingTimer = timers.find((item) => [true, 1, '1', 'true'].includes(item?.is_recording));
      if (recordingTimer) return recordingTimer;

      return timers.find((item) => {
        const state = String(item?.state || '').toLowerCase();
        return state === 'running' || state === 'paused' || state === 'finished';
      }) || null;
    },
    hasRoundSnapshot(payload = {}) {
      const source = this.unwrapRealtimePayload(payload) || {};
      return Boolean(source?.round && typeof source.round === 'object');
    },
    hasRenderableTimerSnapshot(payload = {}) {
      const source = this.unwrapRealtimePayload(payload) || {};
      if (this.hasRoundSnapshot(source)) return true;

      return Boolean(source?.timer && typeof source.timer === 'object');
    },
    hasRenderableCurrentContext() {
      if (this.isVs && this.round) {
        const timers = Array.isArray(this.round?.timers) ? this.round.timers : [];
        if (this.shouldPreserveVsSnapshotContext(this.round, timers)) {
          return true;
        }
      }

      const timerState = String(this.timer?.state || '').toLowerCase();
      if (timerState && timerState !== 'waiting') {
        return true;
      }

      const candidateValues = [
        this.timer?.remaining_ms_snapshot,
        this.timer?.remaining_seconds,
        this.timer?.duration_ms,
        this.timer?.duration_seconds,
        this.timer?.duration,
      ];

      if (candidateValues.some((value) => Number.isFinite(Number(value)))) {
        return true;
      }

      return Boolean(this.leftAthlete || this.activeVsAthlete || this.round);
    },

    shouldIgnoreTransientWaitingPayload(raw = {}, base = {}) {
      const waitingState = String(raw?.status || raw?.mode || base?.status || base?.state || '').toLowerCase();
      if (waitingState !== 'waiting') return false;

      const hasRoundSnapshot = this.hasRoundSnapshot(raw);
      const hasTimerPayload = Boolean(raw?.timer && typeof raw.timer === 'object' && Object.keys(raw.timer).length > 1);
      if (hasRoundSnapshot || hasTimerPayload) return false;

      return this.hasRenderableCurrentContext();
    },
    shouldDelayWaitingTransition(raw = {}, base = {}) {
      const waitingState = String(raw?.status || raw?.mode || base?.status || base?.state || '').toLowerCase();
      if (waitingState !== 'waiting') return false;

      return this.hasRenderableCurrentContext();
    },
    formatSeconds(raw) {
      const total = Number(raw);
      if (!Number.isFinite(total)) return '00:00';
      const clamped = Math.max(0, Math.abs(Math.floor(total)));
      const minutes = Math.floor(clamped / 60).toString().padStart(2, '0');
      const seconds = (clamped % 60).toString().padStart(2, '0');
      return `${minutes}:${seconds}`;
    },
    resolveCurrentPhaseLabel() {
      const round = this.round || {};
      const timer = this.timer || {};
      const competition = round?.competition || timer?.competition || {};
      const stageCalendar = round?.stage_calendar
        ?? round?.stageCalendar
        ?? timer?.stage_calendar
        ?? timer?.stageCalendar
        ?? null;

      const roundNumber = Number(round?.round_number ?? timer?.round_number);
      const isDualQualification = [true, 1, '1', 'true'].includes(
        round?.is_dual_qualification
          ?? round?.competition?.is_dual_qualification
          ?? competition?.is_dual_qualification
          ?? competition?.is_qualification_dual
          ?? timer?.is_dual_qualification
      );

      if (roundNumber === 1) {
        return isDualQualification ? 'Qual. 1' : 'Qual.';
      }

      if (roundNumber === 2 && isDualQualification) {
        return 'Qual. 2';
      }

      return this.resolveStageCalendarLabel(stageCalendar, round, timer);
    },
    resolveStageCalendarLabel(stageCalendar, round = {}, timer = {}) {
      const stageCode = String(stageCalendar?.stage_code || stageCalendar?.stageCode || '').trim().toLowerCase();
      const stageId = Number(
        stageCalendar?.round_size_id
          ?? stageCalendar?.roundSizeId
          ?? round?.round_size_id
          ?? round?.roundSizeId
          ?? timer?.round_size_id
          ?? timer?.roundSizeId
      );

      if (stageCode) {
        const qualificationMatch = stageCode.match(/^qualification_(\d+)$/i) || stageCode.match(/qual(i(f(ication)?)?)?_?(\d+)/i);
        if (qualificationMatch) {
          return `Qual. ${qualificationMatch[1] || qualificationMatch[5] || qualificationMatch[4] || qualificationMatch[3] || qualificationMatch[2] || ''}`.trim();
        }
      }

      const stageMapById = {
        1: 'Final',
        2: 'Semifinal',
        3: 'Quarterfinals',
        4: 'Round of 8',
        5: 'Round of 16',
        6: 'Round of 32',
      };
      if (stageId && stageMapById[stageId]) return stageMapById[stageId];

      const stageMapByCode = {
        stage1: 'Final',
        final: 'Final',
        stage2: 'Semifinal',
        semifinal: 'Semifinal',
        semi_final: 'Semifinal',
        sf: 'Semifinal',
        stage3: 'Quarterfinals',
        quarterfinal: 'Quarterfinals',
        quarter_final: 'Quarterfinals',
        quarterfinals: 'Quarterfinals',
        qf: 'Quarterfinals',
        stage4: 'Round of 16',
        round_16: 'Round of 16',
        round16: 'Round of 16',
        round_of_16: 'Round of 16',
        stage5: 'Round of 32',
        round_32: 'Round of 32',
        round32: 'Round of 32',
        round_of_32: 'Round of 32',
        bronze: '3rd Place',
        third_place: '3rd Place',
        thirdplace: '3rd Place',
        gf: 'Final',
        grand_final: 'Final',
      };
      if (stageCode && stageMapByCode[stageCode]) return stageMapByCode[stageCode];

      if (stageCode.includes('quarter')) return 'Quarterfinals';
      if (stageCode.includes('semi')) return 'Semifinal';
      if (stageCode.includes('round_16') || stageCode.includes('round16') || stageCode.includes('of_16')) return 'Round of 16';
      if (stageCode.includes('round_32') || stageCode.includes('round32') || stageCode.includes('of_32')) return 'Round of 32';
      if (stageCode.includes('final')) return 'Final';

      const explicitName = String(stageCalendar?.name || stageCalendar?.label || '').trim();
      return explicitName;
    },
    extractAthlete(side) {
      const source = this.timer || {};
      const fromTimerLeft = this.normalizeAthlete(source.left || source.athlete || source.primary || (source.targets || source.athletes || [])[0]);
      const fromTimerRight = this.normalizeAthlete(source.right || (source.targets || source.athletes || [])[1]);

      if (side === 'left' && fromTimerLeft) return fromTimerLeft;
      if (side === 'right' && fromTimerRight) return fromTimerRight;

      const roundAthlete = this.getRoundAthlete(side);
      return roundAthlete ? this.normalizeAthlete(roundAthlete) : null;
    },
    normalizeAthlete(raw) {
      if (!raw) return null;
      const firstName = raw.first_name || raw.firstName || raw.user?.name || '';
      const lastName = raw.last_name || raw.lastName || raw.surname || raw.user?.surname || raw.user?.last_name || '';
      const name = raw.full_name || raw.name || [firstName, lastName].filter(Boolean).join(' ');
      const countryCode = raw.country_code || raw.countryCode || raw.country?.code || raw.country?.alpha_2 || '';
      const flagUrl = raw.country_flag || raw.flag || raw.flag_url || raw.flagUrl || raw.country?.flag || '';
      return {
        name: name || '',
        firstName: firstName || '',
        lastName: lastName || '',
        countryCode: countryCode || '',
        flagUrl: flagUrl || '',
      };
    },
    getRoundAthlete(side) {
      if (!this.round || !Array.isArray(this.round.athletes)) return null;
      if (this.round.is_vs) {
        return side === 'left' ? this.round.athletes[0] : this.round.athletes[1];
      }
      return this.round.athletes[0];
    },
    laneRemainingDisplay(side) {
      const key = side === 'left' ? 'left' : 'right';
      const specific = this.timer?.[`remaining_${key}_seconds`] ?? this.timer?.[`${key}Remaining`] ?? this.timer?.[`${key}_remaining`];
      if (Number.isFinite(Number(specific))) return this.formatSeconds(specific);

      const laneTimer = this.getVsLaneTimer(side);
      if (laneTimer) {
        const laneSeconds = this.resolveLaneDisplaySeconds(laneTimer);
        if (Number.isFinite(Number(laneSeconds))) {
          return this.formatSeconds(laneSeconds);
        }
      }

      return this.remainingDisplay;
    },
    laneRemainingSeconds(side) {
      const key = side === 'left' ? 'left' : 'right';
      const specific = this.timer?.[`remaining_${key}_seconds`] ?? this.timer?.[`${key}Remaining`] ?? this.timer?.[`${key}_remaining`];
      if (Number.isFinite(Number(specific))) return Number(specific);

      const laneTimer = this.getVsLaneTimer(side);
      if (laneTimer) {
        const laneSeconds = this.resolveLaneDisplaySeconds(laneTimer);
        if (Number.isFinite(Number(laneSeconds))) return Number(laneSeconds);
      }

      return null;
    },
    getVsLaneTimer(side) {
      if (!this.isVs) return null;
      const athletes = Array.isArray(this.round?.athletes) ? this.round.athletes : [];
      const athlete = side === 'left' ? athletes[0] : athletes[1];
      const laneRoundId = athlete?.round_id || athlete?.roundId;
      if (!laneRoundId) return null;
      return this.vsTimersByRoundId[String(laneRoundId)] || null;
    },
    isLaneOvertime(side) {
      const laneTimer = this.getVsLaneTimer(side);
      if (laneTimer) {
        const laneSeconds = this.resolveLaneDisplaySeconds(laneTimer);
        return Number(laneSeconds) < 0;
      }

      const key = side === 'left' ? 'left' : 'right';
      const specific = this.timer?.[`remaining_${key}_seconds`] ?? this.timer?.[`${key}Remaining`] ?? this.timer?.[`${key}_remaining`];
      return Number(specific) < 0;
    },
    laneStateValue(side) {
      const laneTimer = this.getVsLaneTimer(side);
      if (laneTimer?.state) {
        return String(laneTimer.state).toLowerCase();
      }

      const key = side === 'left' ? 'left' : 'right';
      const specific = this.timer?.[`state_${key}`] ?? this.timer?.[`${key}State`] ?? this.timer?.[`${key}_state`];
      if (specific) {
        return String(specific).toLowerCase();
      }

      return (this.timer?.state || 'idle').toLowerCase();
    },
    laneIsRecording(side) {
      const laneTimer = this.getVsLaneTimer(side);
      if (laneTimer) {
        return [true, 1, '1', 'true'].includes(laneTimer.is_recording);
      }

      const key = side === 'left' ? 'left' : 'right';
      const specific = this.timer?.[`is_recording_${key}`]
        ?? this.timer?.[`${key}IsRecording`]
        ?? this.timer?.[`${key}_is_recording`];

      if (specific !== undefined) {
        return [true, 1, '1', 'true'].includes(specific);
      }

      return false;
    },
    laneStateLabel(side) {
      const state = this.laneStateValue(side);
      if (state === 'waiting') return this.$t?.('judge.timer.state_waiting')?.toUpperCase?.() || 'WAITING';
      return state.toUpperCase();
    },
    laneStateClass(side) {
      const state = this.laneStateValue(side);
      return {
        'status-pill--running': state === 'running',
        'status-pill--paused': state === 'paused',
        'status-pill--idle': !state || state === 'idle',
        'status-pill--finished': state === 'finished',
        'status-pill--waiting': state === 'waiting',
      };
    },
    isAllowedTheme(theme) {
      return ALLOWED_TIMER_THEMES.includes(String(theme || '').toLowerCase());
    },
    normalizeTheme(theme) {
      return normalizeTimerTheme(theme);
    },
    applyBodyThemeClass() {
      if (typeof document === 'undefined') return;
      const body = document.body;
      const normalizedTheme = this.normalizeTheme(this.theme);
      body.classList.remove(...ALLOWED_TIMER_THEMES.map((theme) => `timer-theme-${theme}`));
      body.classList.add(`timer-theme-${normalizedTheme}`);
    },
    clearBodyThemeClass() {
      if (typeof document === 'undefined') return;
      document.body.classList.remove(...ALLOWED_TIMER_THEMES.map((theme) => `timer-theme-${theme}`));
    },
    clearLegacyTimerStorage() {
      ['timerForceSync', 'timerTheme'].forEach((key) => {
        try {
          window?.localStorage?.removeItem(key);
        } catch (e) {
          /* ignore */
        }

        try {
          window?.sessionStorage?.removeItem(key);
        } catch (e) {
          /* ignore */
        }
      });
    },
    applyTimerPayload(raw) {
      if (!raw) return;

      raw = this.unwrapRealtimePayload(raw);
      if (!raw) return;

      this.clearPendingWaitingState();

      raw = this.normalizeSnapshotPayload(raw);

      const base = this.normalizeIncomingTimer(raw.timer || raw, raw);
      const hasContextSnapshot = this.hasRoundSnapshot(raw) || String(raw.status || base.status || '').toLowerCase() === 'waiting';
      this.competitionId = raw.competition_id || raw.competitionId || this.competitionId || null;
      if (this.isAllowedTheme(raw.theme)) {
        this.theme = this.normalizeTheme(raw.theme);
        this.applyBodyThemeClass();
      }
      const incomingRoundId = base.round_id || base.roundId;
      const previousRoundId = this.timer?.round_id || this.timer?.roundId;
      const roundChanged = incomingRoundId && String(previousRoundId || '') !== String(incomingRoundId);
      const version = this.resolvePayloadVersion(base, raw);
      if (version && this.lastVersion && version < this.lastVersion && !roundChanged) {
        return; // Ignore out-of-order/stale events (unless round changed)
      }
      if (version) this.lastVersion = roundChanged ? version : Math.max(this.lastVersion || 0, version);

      const serverNowMs = this.resolveServerNow(base, raw);
      if (serverNowMs) {
        this.serverDriftMs = Date.now() - serverNowMs;
      }

      this.applyCompetitionCode(raw);

      if (this.shouldIgnoreTransientWaitingPayload(raw, base)) {
        return;
      }

      if ((raw.status || base.status || '').toLowerCase() === 'waiting') {
        const messageKey = raw.message || base.message || 'waiting_selection';
        const payloadFromKey = messageKey && (raw[messageKey] || base[messageKey]);
        const resolvedMessage = payloadFromKey || base.waiting_selection || 'Waiting for competitor selection';
        const compName = base.competition_name || base.competitionName || raw.competition_name || raw.competitionName || '';

        if (this.shouldDelayWaitingTransition(raw, base)) {
          this.scheduleWaitingState({ resolvedMessage, compName });
          return;
        }

        this.applyWaitingState({ resolvedMessage, compName });
        return;
      }

      this.waitingActive = false;
      this.waitingMessage = '';
      this.waitingCompetitionName = '';

      if (raw.round) {
        this.round = this.normalizeRoundPayload(raw.round, raw);
      }
      this.mergeIncomingTimerIntoRound(base, incomingRoundId);
      const nextState = (base.state || this.timer?.state || 'idle').toLowerCase();
      const timerSeed = roundChanged ? {} : (this.timer || {});
      this.timer = this.normalizeIncomingTimer({
        ...timerSeed,
        ...base,
        round_id: incomingRoundId || timerSeed.round_id,
        is_dual_qualification: base.is_dual_qualification ?? raw.is_dual_qualification ?? timerSeed.is_dual_qualification,
        version_ms: version || base.version_ms || timerSeed.version_ms,
        version: version || base.version || timerSeed.version,
        updated_at: base.updated_at || timerSeed.updated_at,
        server_now_ms: serverNowMs || base.server_now_ms || timerSeed.server_now_ms,
        server_now: base.server_now || raw.server_now || timerSeed.server_now,
      }, raw);
      this.lastState = nextState;
      this.ensureTicking();

      if (this.skipNextResync) {
        this.skipNextResync = false;
      }

      // Si cambiamos de round o aún no tenemos detalles del round, rehidratar desde el backend
      if ((roundChanged && !hasContextSnapshot) || (!this.round && incomingRoundId && !hasContextSnapshot)) {
        this.syncTimerFromServer();
      }
    },
    normalizeSnapshotPayload(raw = {}) {
      if (raw?.timer) return raw;

      const snapshotRound = raw?.round && typeof raw.round === 'object' ? raw.round : null;
      const timers = Array.isArray(snapshotRound?.timers) ? snapshotRound.timers : [];
      const hasActiveRoundKey = Object.prototype.hasOwnProperty.call(raw || {}, 'active_round_id')
        || Object.prototype.hasOwnProperty.call(raw || {}, 'activeRoundId');
      const hasModeKey = Object.prototype.hasOwnProperty.call(raw || {}, 'mode');
      const hasAccessCodeKey = Object.prototype.hasOwnProperty.call(raw || {}, 'access_code')
        || Object.prototype.hasOwnProperty.call(raw || {}, 'accessCode');
      const isSnapshotPayload = Boolean(snapshotRound || hasActiveRoundKey || hasModeKey || hasAccessCodeKey);

      if (!isSnapshotPayload) {
        return raw;
      }

      const activeRoundId = raw?.active_round_id ?? raw?.activeRoundId ?? null;
      const mode = String(raw?.mode || raw?.status || '').toLowerCase();
      const isWaitingSnapshot = mode === 'waiting' || (hasActiveRoundKey && activeRoundId === null);

      if (snapshotRound) {
        this.round = this.normalizeRoundPayload(snapshotRound, raw);
      }

      if (raw?.access_code || raw?.accessCode) {
        this.competitionCode = raw.access_code || raw.accessCode;
      }

      const preserveVsContext = this.shouldPreserveVsSnapshotContext(snapshotRound, timers);

      if (isWaitingSnapshot && !preserveVsContext) {
        return this.buildWaitingSnapshotPayload(raw, snapshotRound);
      }

      const activeTimer = this.resolveSnapshotActiveTimer(timers, activeRoundId, hasActiveRoundKey);

      if (!activeTimer) {
        return this.buildWaitingSnapshotPayload(raw, snapshotRound);
      }

      return {
        ...raw,
        timer: {
          ...activeTimer,
          competition_code: raw?.access_code || raw?.accessCode || activeTimer?.competition_code || activeTimer?.competitionCode,
          is_dual_qualification: activeTimer?.is_dual_qualification ?? raw?.is_dual_qualification,
          version_ms: activeTimer?.version_ms ?? raw?.version_ms,
          version: activeTimer?.version_ms ?? raw?.version_ms ?? activeTimer?.version ?? raw?.version,
          server_now_ms: activeTimer?.server_now_ms ?? raw?.server_now_ms,
          server_now: activeTimer?.server_now ?? raw?.server_now,
        },
      };
    },
      applyCompetitionCode(raw = {}) {
        const base = raw.timer || raw;
        const code = base.competition_code || raw.competition_code || this.round?.competition_code || this.round?.competition?.code || '';
        if (code && code !== this.competitionCode) {
          this.competitionCode = code;
          this.resetChannel();
          this.subscribeToChannel();
        }
      },
    async init() {
      this.loading = true;
      this.error = '';
      try {
        const resp = await this.$axios.get(`/timer-code/${this.accessCode}/state`);
        this.applyTimerPayload(resp?.data || {});
        this.subscribeToChannel();
      } catch (err) {
        this.error = err?.response?.data?.message || this.$t('judge.timer.invalid_code') || 'Código inválido o expirado';
      } finally {
        this.loading = false;
      }
    },
    async syncTimerFromServer() {
      try {
        const resp = await this.$axios.get(`/timer-code/${this.accessCode}/state`);
        this.skipNextResync = true;
        this.applyTimerPayload(resp?.data || {});
      } catch (e) {
        console.warn('syncTimerFromServer failed', e?.response?.data || e?.message);
      }
    },
    async setActiveTimer(roundId = null) {
      const competitionId = this.competitionId || this.competitionIdFromRound();
      if (!competitionId) return;
      try {
        await this.$axios.post('/timers/active', { competition_id: competitionId, round_id: roundId });
      } catch (e) {
        console.warn('setActiveTimer failed', e?.response?.data || e?.message);
      }
    },
    competitionIdFromRound() {
      return this.round?.competition_id || this.round?.competitionId || null;
    },
    subscribeToChannel() {
      const fullName = this.channelFullName;
      if (!fullName || (this.channel && this.currentChannelName === fullName)) return;

      this.resetChannel();

      const handler = (payload) => this.applyTimerPayload(payload || {});
      const activeHandler = (payload) => {
        const source = this.unwrapRealtimePayload(payload || {});
        if (!this.matchesTimerPayload(source)) return;
        if (this.hasRenderableTimerSnapshot(source || {})) {
          this.applyTimerPayload(source || {});
          return;
        }
        this.syncTimerFromServer();
      };
      const themeHandler = (payload) => {
        const source = this.unwrapRealtimePayload(payload || {});
        if (!this.matchesTimerPayload(source)) return;
        const nextTheme = source?.theme;
        if (this.isAllowedTheme(nextTheme)) {
          this.theme = this.normalizeTheme(nextTheme);
          this.applyBodyThemeClass();
        }
      };
      try {
        // Canal público timer-code.{competition_code}
        this.channel = echoService.channel(fullName);
      } catch (e) {
        this.error = this.$t('judge.timer.ws_error') || 'No se pudo suscribir al canal';
        return;
      }

      this.currentChannelName = fullName;

      this.channel.listen('.CompetitionTimerUpdated', handler);
      this.channel.listen('.CompetitionRoundContextUpdated', handler);
      this.channel.listen('.CompetitionActiveTimerChanged', activeHandler);
      this.channel.listen('.CompetitionTimerThemeChanged', themeHandler);
    },
    matchesTimerPayload(payload = {}) {
      const source = this.unwrapRealtimePayload(payload) || {};
      const payloadCode = String(source?.access_code || source?.accessCode || '').trim();
      const currentCode = String(this.accessCode || this.competitionCode || '').trim();
      if (payloadCode && currentCode) {
        return payloadCode === currentCode;
      }

      const payloadCompetitionId = Number(source?.competition_id || source?.competitionId);
      const currentCompetitionId = Number(this.competitionId || this.competitionIdFromRound());
      return Number.isFinite(payloadCompetitionId) && Number.isFinite(currentCompetitionId)
        ? payloadCompetitionId === currentCompetitionId
        : true;
    },
    resetChannel() {
      if (this.channel) {
        try {
          this.channel.stopListening('.CompetitionTimerUpdated');
          this.channel.stopListening('.CompetitionRoundContextUpdated');
          this.channel.stopListening('.CompetitionActiveTimerChanged');
          this.channel.stopListening('.CompetitionTimerThemeChanged');
        } catch (e) {
          /* ignore */
        }
      }

      if (this.currentChannelName) {
        try {
          echoService.leave(this.currentChannelName);
        } catch (e) {
          /* ignore */
        }
      }

      this.channel = null;
      this.currentChannelName = '';
    },
    ensureTicking() {
      if (this.tickHandle) {
        clearInterval(this.tickHandle);
        this.tickHandle = null;
      }

      this.tickNowMs = Date.now();
      if (!this.hasAnyRunningTimer) return;

      this.tickHandle = setInterval(() => {
        this.tickNowMs = Date.now();
      }, 50);
    },
    stopTick() {
      if (this.tickHandle) {
        clearInterval(this.tickHandle);
        this.tickHandle = null;
      }
    },
    resolvePayloadVersion(base = {}, raw = {}) {
      return this.normalizeVersionMs(
        base.version_ms ?? raw.version_ms ?? base.version ?? raw.version,
        base.updated_at ?? raw.updated_at
      );
    },
    resolveServerNow(base = {}, raw = {}) {
      const direct = this.normalizeTimestampMs(base.server_now_ms ?? raw.server_now_ms ?? base.server_now ?? raw.server_now);
      return Number.isFinite(direct) ? direct : 0;
    },
    computeRemainingSeconds(base = {}, state = 'idle', serverNowMs = 0) {
      const remainingMsSnapshot = Number(base.remaining_ms_snapshot);
      if (Number.isFinite(remainingMsSnapshot)) {
        if (state === 'running' && serverNowMs) {
          const elapsedMs = Math.max(0, this.resolveCurrentServerNowMs(base) - serverNowMs);
          return (remainingMsSnapshot - elapsedMs) / 1000;
        }
        return remainingMsSnapshot / 1000;
      }

      const rawRemaining = base.remaining_seconds ?? base.remaining ?? this.timer?.remaining_seconds ?? 0;
      const remaining = Number(rawRemaining);
      if (!Number.isFinite(remaining)) return 0;
      if (state === 'running' && serverNowMs) {
        const elapsed = Math.max(0, (this.tickNowMs - serverNowMs) / 1000);
        return remaining - elapsed;
      }
      return remaining;
    },
    resolveCurrentServerNowMs(timer = {}) {
      if (Number.isFinite(this.serverDriftMs)) {
        return this.tickNowMs - this.serverDriftMs;
      }

      const clientReceivedAtMs = Number(timer?.client_received_at_ms);
      const serverNowMs = Number(timer?.server_now_ms);
      if (Number.isFinite(clientReceivedAtMs) && Number.isFinite(serverNowMs)) {
        return serverNowMs + Math.max(0, this.tickNowMs - clientReceivedAtMs);
      }

      return this.tickNowMs;
    },
    resolveTimerDurationSeconds(timer = {}) {
      const durationMs = Number(timer?.duration_ms);
      if (Number.isFinite(durationMs)) {
        return durationMs / 1000;
      }

      const duration = Number(timer?.duration_seconds ?? timer?.duration);
      return Number.isFinite(duration) ? duration : 0;
    },
    resolveTimerDisplaySeconds(timer = {}) {
      const state = String(timer?.state || 'idle').toLowerCase();
      const remainingMsSnapshot = Number(timer?.remaining_ms_snapshot);
      const durationMs = Number(timer?.duration_ms);
      const explicitRemainingSeconds = Number(timer?.remaining_seconds ?? timer?.remaining);

      if (state === 'paused' && Number.isFinite(explicitRemainingSeconds)) {
        return explicitRemainingSeconds;
      }

      if (state === 'running' && Number.isFinite(remainingMsSnapshot)) {
        const serverNowMs = Number(timer?.server_now_ms);
        const currentServerNowMs = this.resolveCurrentServerNowMs(timer);
        const elapsedMs = Number.isFinite(serverNowMs)
          ? Math.max(0, currentServerNowMs - serverNowMs)
          : Math.max(0, this.tickNowMs - Number(timer?.client_received_at_ms || this.tickNowMs));
        return (remainingMsSnapshot - elapsedMs) / 1000;
      }

      const remaining = explicitRemainingSeconds;
      const duration = Number.isFinite(durationMs)
        ? durationMs / 1000
        : Number(timer?.duration_seconds ?? timer?.duration);

      if (state === 'running' || state === 'paused' || state === 'finished') {
        if (Number.isFinite(remainingMsSnapshot)) return remainingMsSnapshot / 1000;
        if (Number.isFinite(remaining)) return remaining;
        if (Number.isFinite(duration)) return duration;
        return 0;
      }

      if (Number.isFinite(duration)) return duration;
      if (Number.isFinite(remaining)) return remaining;
      return 0;
    },
    resolveLaneDisplaySeconds(timer = {}) {
      return this.resolveTimerDisplaySeconds(timer);
    },
    mergeIncomingTimerIntoRound(base = {}, incomingRoundId = null) {
      if (!incomingRoundId || !this.round || !Array.isArray(this.round.timers)) return;

      const mergedTimers = [...this.round.timers];
      const idx = mergedTimers.findIndex((item) => String(item?.round_id || item?.roundId) === String(incomingRoundId));
      const previous = idx >= 0 ? mergedTimers[idx] : {};
      const merged = {
        ...previous,
        ...base,
        round_id: incomingRoundId,
        roundId: incomingRoundId,
      };
      const incomingState = String(merged?.state || '').toLowerCase();
      const incomingDuration = this.resolveTimerDurationSeconds(merged);
      const incomingCanShareDuration = !incomingState || incomingState === 'idle' || incomingState === 'waiting';
      const canPropagateSharedDuration = Number.isFinite(incomingDuration)
        && incomingCanShareDuration
        && Array.isArray(this.round?.athletes)
        && this.round.athletes.length >= 2
        && mergedTimers.length >= 2;

      if (idx >= 0) {
        mergedTimers.splice(idx, 1, merged);
      } else {
        mergedTimers.push(merged);
      }

      if (canPropagateSharedDuration) {
        for (let i = 0; i < mergedTimers.length; i += 1) {
          const item = mergedTimers[i] || {};
          const itemRoundId = item?.round_id || item?.roundId;
          if (String(itemRoundId || '') === String(incomingRoundId)) continue;

          const itemState = String(item?.state || '').toLowerCase();
          if (itemState === 'running' || itemState === 'paused' || itemState === 'finished') continue;

          mergedTimers[i] = {
            ...item,
            duration_ms: incomingDuration * 1000,
            duration_seconds: incomingDuration,
            duration: incomingDuration,
            remaining_ms_snapshot: incomingDuration * 1000,
            remaining_seconds: incomingDuration,
          };
        }
      }

      this.round = {
        ...this.round,
        timers: mergedTimers,
      };
    },
    async toggleFullscreen() {
      try {
        if (!document.fullscreenElement) {
          await document.documentElement.requestFullscreen();
        } else if (document.exitFullscreen) {
          await document.exitFullscreen();
        }
      } catch (e) {
        console.warn('fullscreen toggle failed', e?.message || e);
      }
    },
    handleFullscreenChange() {
      this.isFullscreen = Boolean(document.fullscreenElement);
    },
  },
};
</script>

<style scoped>
.timer-code-page {
  box-sizing: border-box;
  min-height: 100vh;
  background: #090d18;
  color: #f8fafc;
  display: block;
  padding: 0;
  width: 100vw;
  overflow: hidden;
}

.timer-code-shell {
  width: 100%;
  height: 100vh;
  color: inherit;
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: clamp(10px, 2.6vw, 20px);
  background: transparent;
}

.fullscreen-btn {
  background: rgba(255, 255, 255, 0.09);
  color: #f8fafc;
  border: 1px solid rgba(255, 255, 255, 0.35);
  border-radius: 8px;
  padding: 6px 10px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.fullscreen-btn:hover {
  background: rgba(255, 255, 255, 0.17);
}

.theme-light .fullscreen-btn {
  color: #0f172a;
  background: rgba(15, 23, 42, 0.04);
  border-color: rgba(15, 23, 42, 0.25);
}

.theme-light .fullscreen-btn:hover {
  background: rgba(15, 23, 42, 0.1);
}

.fullscreen-btn--corner {
  position: fixed;
  right: 16px;
  bottom: 16px;
  z-index: 15;
}

:global(.app-navbar) {
  display: none !important;
}

:global(body.hide-app-nav .custom-navbar),
:global(body.hide-app-nav .navbar),
:global(body.hide-app-nav .navbar-brand),
:global(body.hide-app-nav .navbar-menu) {
  display: none !important;
}

:global(body.hide-app-nav main) {
  margin-top: 0 !important;
  padding-top: 0 !important;
}

:global(body.timer-theme-dark) {
  background: #090d18 !important;
  overflow: hidden;
}

:global(body.timer-theme-light) {
  background: #ffffff !important;
  overflow: hidden;
}

:global(body.timer-theme-custom) {
  background: #090d18 !important;
  overflow: hidden;
}

:global(body.timer-theme-custom_2) {
  background: #090d18 !important;
  overflow: hidden;
}

.panel {
  border-radius: 0;
  padding: clamp(24px, 8vw, 50px);
  box-shadow: none;
  width: min(100vw, 1800px);
  margin: 0 auto;
}

.panel--light {
  background: transparent;
  color: inherit;
  min-height: clamp(320px, 45vh, 900px);
  display: flex;
  align-items: center;
  justify-content: center;
}

.panel--overtime {
  background: transparent;
  color: #ef4444;
  min-height: clamp(400px, 55vh, 1100px);
  display: flex;
  align-items: center;
  justify-content: center;
}

.panel--neutral {
  background: transparent;
  color: inherit;
  text-align: center;
}

.panel--waiting {
  min-height: clamp(360px, 52vh, 1100px);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: clamp(1.4rem, 3vw, 2.8rem);
  font-weight: 800;
  padding: 32px;
  line-height: 1.25;
}

.waiting-message {
  display: flex;
  flex-direction: column;
  gap: 10px;
  align-items: center;
}

.waiting-text {
  font-size: clamp(1.6rem, 3.5vw, 3rem);
  font-weight: 800;
  letter-spacing: 0.2px;
}

.waiting-competition {
  font-size: clamp(1rem, 2.2vw, 1.6rem);
  font-weight: 600;
  letter-spacing: 1px;
  text-transform: uppercase;
  opacity: 0.82;
}

.panel--error {
  background: transparent;
  color: #ef4444;
  text-align: center;
}

.meta-row {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
  gap: 12px;
  margin-bottom: 16px;
}

.meta-label { color: #6b7280; text-transform: uppercase; letter-spacing: 0.3px; }
.meta-value { font-weight: 800; font-size: 1.2rem; }

.lanes { display: grid; grid-template-columns: 1fr; gap: 14px; }
.lanes--vs { grid-template-columns: 1fr 1fr; }

.lane {
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  padding: 14px;
  background: #f8fafc;
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.7);
}

.lane-header { display: flex; align-items: center; gap: 10px; margin-bottom: 10px; }
.lane-name { font-weight: 700; font-size: 1.05rem; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }

@keyframes pulse-red {
  0% { box-shadow: 0 0 0 rgba(139, 28, 28, 0.35); }
  50% { box-shadow: 0 0 28px rgba(139, 28, 28, 0.8); }
  100% { box-shadow: 0 0 0 rgba(139, 28, 28, 0.35); }
}

:deep(flag) {
  width: 88px;
  height: 60px;
  display: inline-block;
}

:deep(flag svg),
:deep(flag img) {
  width: 100% !important;
  height: 100% !important;
  display: block;
}

.lane-timer {
  font-size: 2.4rem;
  font-weight: 800;
  color: inherit;
  text-align: center;
}

@media (max-width: 1024px) {
  .panel {
    width: 96vw;
    padding: clamp(20px, 6vw, 36px);
  }
}

@media (max-width: 768px) {
  .panel {
    width: 100vw;
    padding: clamp(18px, 6vw, 32px);
  }
}

@media (max-width: 540px) {
  .panel {
    padding: clamp(14px, 7vw, 22px);
  }
}

.theme-light {
  background: #ffffff;
  color: #0f172a;
}

.theme-light .timer-code-page {
  background: #ffffff;
}

.theme-light .timer-code-shell {
  background: #ffffff;
}

.theme-light .panel,
.theme-light .panel--neutral,
.theme-light .panel--waiting,
.theme-light .panel--light {
  background: transparent;
  color: #0f172a;
}

.theme-light .waiting-text,
.theme-light .waiting-competition {
  color: #0f172a;
}

.theme-light .panel--error {
  color: #dc2626;
}

.theme-custom .timer-code-page,
.theme-custom .timer-code-shell {
  background: #090d18;
}
</style>
